import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle, Clock, DollarSign, Calendar, User, Mail, Phone, MapPin, Building, CreditCard, Package, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPackagePurchase() {
  const { packageId } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address_line_1: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    fetchPackage();
  }, [packageId]);

  const fetchPackage = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/packages/public/${packageId}`);
      setPkg(res.data);
    } catch {
      toast.error('Package not found');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address_line_1.trim()) newErrors.address_line_1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pin code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayNow = async (token) => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: Create the package purchase record first
      await axios.post(`${BACKEND_URL}/api/packages/public/purchase`, {
        package_id: packageId,
        ...formData,
        payment_method: 'clover'
      });

      // Step 2: Create Clover Hosted Checkout session
      const nameParts = formData.full_name.split(' ');
      const res = await axios.post(`${BACKEND_URL}/api/clover/hosted-checkout`, {
        customer_email: formData.email,
        customer_first_name: nameParts[0] || '',
        customer_last_name: nameParts.slice(1).join(' ') || '',
        customer_phone: formData.phone,
        items: [{
          name: pkg.name,
          price: Math.round(pkg.price * 100),
          unitQty: 1,
          note: `${pkg.hours} hour service package`
        }],
        package_id: packageId,
        return_path: `/purchase/package/${packageId}/success`
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to start payment');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <Card className="max-w-md shadow-xl">
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-900 mb-2">Package Not Found</p>
            <p className="text-gray-500">This package may have been removed or the link is invalid.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (purchased) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <Header />
        <div className="flex items-center justify-center py-16 px-4">
          <Card className="max-w-lg w-full shadow-xl border-0">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3" data-testid="purchase-success-title">
                Purchase Successful!
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Your <strong>{pkg.name}</strong> package has been activated.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Package Details:</p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Hours:</strong> {pkg.hours} hours</p>
                  <p><strong>Validity:</strong> {pkg.validity_months} months</p>
                  <p><strong>Amount Paid:</strong> ${pkg.price}</p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 text-left">
                <p className="text-sm text-teal-800 font-medium mb-2">What happens next?</p>
                <ul className="text-sm text-teal-700 space-y-1">
                  <li>- A confirmation email has been sent to you</li>
                  <li>- Your package is now active in your customer portal</li>
                  <li>- Our team will reach out to schedule your first service</li>
                </ul>
              </div>
              <p className="text-gray-400 text-sm mt-6">You can close this window now.</p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 pb-24">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Package Details Card */}
        <Card className="shadow-xl border-0 overflow-hidden mb-6" data-testid="package-details-card">
          <div className="bg-gradient-to-r from-[#595959] to-[#777] px-6 py-5 text-white">
            <div className="flex items-center gap-3 mb-1">
              <Package className="w-6 h-6" />
              <h2 className="text-2xl font-bold">{pkg.name}</h2>
            </div>
            <p className="text-gray-200 text-sm">{pkg.description || 'Professional service package'}</p>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <div className="flex items-center justify-center mb-2">
                  <Clock className="w-8 h-8 text-[#595959]" />
                </div>
                <div className="text-3xl font-bold text-gray-900">{pkg.hours}</div>
                <div className="text-sm text-gray-500">Hours</div>
              </div>
              <div>
                <div className="flex items-center justify-center mb-2">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">${pkg.price}</div>
                <div className="text-sm text-gray-500">Total Price</div>
              </div>
              <div>
                <div className="flex items-center justify-center mb-2">
                  <Calendar className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900">{pkg.validity_months}</div>
                <div className="text-sm text-gray-500">Months Valid</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-gray-500">Effective Rate: <strong>${(pkg.price / pkg.hours).toFixed(2)}/hour</strong></p>
            </div>

            {!showForm && (
              <Button
                className="w-full mt-6 h-12 text-lg font-semibold bg-[#595959] hover:bg-[#444]"
                onClick={() => setShowForm(true)}
                data-testid="purchase-package-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Purchase This Package
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Purchase Form */}
        {showForm && (
          <Card className="shadow-xl border-0 overflow-hidden" data-testid="purchase-form-card">
            <div className="bg-gradient-to-r from-[#595959] to-[#777] px-6 py-4 text-white">
              <h3 className="text-lg font-bold">Complete Your Purchase</h3>
              <p className="text-gray-200 text-sm">Fill in your details to purchase {pkg.name}</p>
            </div>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Personal Info */}
                <div className="space-y-4">
                  <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                    <User className="w-5 h-5 text-[#595959]" />
                    Personal Information
                  </h4>
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      data-testid="purchase-name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className={errors.name ? 'border-red-500' : ''}
                    />
                    {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                        <Mail className="w-4 h-4 inline mr-1 text-gray-400" />
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="purchase-email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="your@email.com"
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                        <Phone className="w-4 h-4 inline mr-1 text-gray-400" />
                        Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="purchase-phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit phone number"
                        className={errors.phone ? 'border-red-500' : ''}
                      />
                      {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-4">
                  <h4 className="text-base font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                    <Building className="w-5 h-5 text-[#595959]" />
                    Property Address
                  </h4>
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-sm font-medium text-gray-700">
                      <MapPin className="w-4 h-4 inline mr-1 text-gray-400" />
                      Address Line <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address"
                      data-testid="purchase-address"
                      value={formData.address_line_1}
                      onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                      placeholder="Street address, apartment, suite, etc."
                      className={errors.address_line_1 ? 'border-red-500' : ''}
                    />
                    {errors.address_line_1 && <p className="text-red-500 text-xs">{errors.address_line_1}</p>}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        City <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        data-testid="purchase-city"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="City"
                        className={errors.city ? 'border-red-500' : ''}
                      />
                      {errors.city && <p className="text-red-500 text-xs">{errors.city}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        State <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        data-testid="purchase-state"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="State"
                        className={errors.state ? 'border-red-500' : ''}
                      />
                      {errors.state && <p className="text-red-500 text-xs">{errors.state}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">
                        Pin Code <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        data-testid="purchase-pincode"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Pin code"
                        className={errors.pincode ? 'border-red-500' : ''}
                      />
                      {errors.pincode && <p className="text-red-500 text-xs">{errors.pincode}</p>}
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">Order Summary</h4>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{pkg.name}</span>
                    <span className="font-medium">${pkg.price}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Hours Included</span>
                    <span>{pkg.hours} hours</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Validity</span>
                    <span>{pkg.validity_months} months</span>
                  </div>
                  <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-green-700">${pkg.price}</span>
                  </div>
                </div>

                {/* Pay Now Button - Redirects to Clover Hosted Checkout */}
                <Button
                  className="w-full h-12 text-lg font-semibold bg-green-600 hover:bg-green-700"
                  onClick={() => handlePayNow()}
                  disabled={submitting}
                  data-testid="pay-now-btn"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Redirecting to payment...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay Now - ${pkg.price}
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <Shield className="w-4 h-4" />
                  <span>You'll be redirected to Clover's secure payment page</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trust Badges */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <Shield className="w-6 h-6 mx-auto mb-1 text-[#595959]" />
            <p className="text-xs text-gray-600">Licensed & Insured</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <Clock className="w-6 h-6 mx-auto mb-1 text-[#595959]" />
            <p className="text-xs text-gray-600">Flexible Hours</p>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <DollarSign className="w-6 h-6 mx-auto mb-1 text-[#595959]" />
            <p className="text-xs text-gray-600">Best Value</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <div className="bg-white border-b-4 border-[#595959] shadow-sm">
      <div className="max-w-4xl mx-auto px-4 py-5">
        <div className="flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_a43a1576-2447-420f-b8ba-eeae8c53cffe/artifacts/a7oiuv0k_Black%20-%20With%20ROC%20number.png"
            alt="Redefyne Logo"
            className="w-20 h-20 object-contain"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Redefyne - Interior and Exterior</h1>
            <p className="text-gray-500 text-sm mt-1">
              2301 South Stearman Drive | Chandler, Arizona 85286
            </p>
            <p className="text-gray-500 text-sm">
              602-715-1313 | support@redefyneaz.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white py-4 text-center text-sm">
      <p>Redefyne Interior & Exterior | 2301 S Stearman Dr, Chandler, AZ 85286 | 602-715-1313</p>
    </div>
  );
}
